import React, { useEffect, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TicketSubjectModal from "../../components/TicketSubjectModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKET_SUBJECTS") {
    const ticketSubjects = action.payload;
    const nextState = [...state];

    ticketSubjects.forEach(ticketSubject => {
      const ticketSubjectIndex = nextState.findIndex(
        item => item.id === ticketSubject.id
      );
      if (ticketSubjectIndex !== -1) {
        nextState[ticketSubjectIndex] = ticketSubject;
      } else {
        nextState.push(ticketSubject);
      }
    });

    return nextState;
  }

  if (action.type === "UPSERT_TICKET_SUBJECT") {
    const ticketSubject = action.payload;
    const ticketSubjectIndex = state.findIndex(
      item => item.id === ticketSubject.id
    );

    if (ticketSubjectIndex !== -1) {
      state[ticketSubjectIndex] = ticketSubject;
      return [...state];
    }

    return [ticketSubject, ...state];
  }

  if (action.type === "DELETE_TICKET_SUBJECT") {
    return state.filter(item => item.id !== action.payload);
  }

  return state;
};

const TicketSubjects = () => {
  const classes = useStyles();
  const [ticketSubjects, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [ticketSubjectModalOpen, setTicketSubjectModalOpen] = useState(false);
  const [selectedTicketSubject, setSelectedTicketSubject] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ticket-subjects");
        dispatch({ type: "LOAD_TICKET_SUBJECTS", payload: data });
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("ticketSubject", data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT_TICKET_SUBJECT", payload: data.ticketSubject });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_TICKET_SUBJECT",
          payload: data.ticketSubjectId
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenTicketSubjectModal = () => {
    setSelectedTicketSubject(null);
    setTicketSubjectModalOpen(true);
  };

  const handleCloseTicketSubjectModal = () => {
    setSelectedTicketSubject(null);
    setTicketSubjectModalOpen(false);
  };

  const handleEditTicketSubject = ticketSubject => {
    setSelectedTicketSubject(ticketSubject);
    setTicketSubjectModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedTicketSubject(null);
  };

  const handleDeleteTicketSubject = async ticketSubjectId => {
    try {
      await api.delete(`/ticket-subjects/${ticketSubjectId}`);
      toast.success("Assunto removido com sucesso");
    } catch (error) {
      toastError(error);
    }
    setSelectedTicketSubject(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={selectedTicketSubject ? `Remover assunto ${selectedTicketSubject.name}?` : ""}
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteTicketSubject(selectedTicketSubject.id)}
      >
        Essa acao remove o assunto da lista de encerramento.
      </ConfirmationModal>
      <TicketSubjectModal
        open={ticketSubjectModalOpen}
        onClose={handleCloseTicketSubjectModal}
        ticketSubjectId={selectedTicketSubject?.id}
      />
      <MainHeader>
        <Title>Assuntos do Chamado</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenTicketSubjectModal}>
            Novo assunto
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Assunto</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticketSubjects.map(ticketSubject => (
              <TableRow key={ticketSubject.id}>
                <TableCell align="center">{ticketSubject.name}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={ticketSubject.isActive ? "Ativo" : "Inativo"}
                    color={ticketSubject.isActive ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEditTicketSubject(ticketSubject)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedTicketSubject(ticketSubject);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={3} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TicketSubjects;
